import { useState } from "react";
import { CircleUserRound, Copy, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { InstallButton } from "@/components/InstallPrompt";
import { PushToggle } from "@/components/PushToggle";
import { PhoneGate } from "@/components/PhoneGate";
import { api, displayTier, roleLabel } from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] font-semibold uppercase tracking-[.12em] text-brand-subtle">{label}</span>
    <b className={`${label === "Email" ? "break-all" : "break-words"} text-[15px] font-semibold text-brand-ink`}>{value || "—"}</b>
  </div>
);

export const ProfileDialog = ({ role, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/members/me");
      setMember(data);
    } catch (err) {
      // Admins (and other roles) may not have a member profile.
      if (err?.response?.status === 404) setMember(null);
      else setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) load();
  };

  const copyCode = async () => {
    await navigator.clipboard.writeText(member.referral_code);
    toast.success("Referral code copied");
  };

  const tier = displayTier(member ? { ...member, role: member.role || role } : null);

  const phoneDone = async () => {
    setPhoneOpen(false);
    await load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="profile-button"
          aria-label="Profile"
          className="h-11 rounded-[13px] border-black/10 bg-white/80 px-3 shadow-sm"
        >
          <CircleUserRound size={16} /><span className="hidden sm:inline">Profile</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[440px] rounded-[18px]">
        <DialogHeader>
          <DialogTitle className="brand-title text-xl">Your profile</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-10">
            <Loader2 className="animate-spin text-brand-muted" size={22} />
          </div>
        ) : error ? (
          <p className="py-6 text-sm text-red-600">{error}</p>
        ) : member ? (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[#0a2724] text-brand-gold-light">
                <CircleUserRound size={24} />
              </div>
              <div>
                <p className="text-[17px] font-semibold text-brand-ink">{member.name}</p>
                <p className="text-sm text-brand-muted">{roleLabel(member.role || role)}</p>
              </div>
            </div>

            {member.referral_code && (
              <div className="flex items-center justify-between rounded-[12px] bg-brand-gold-soft px-4 py-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[.12em] text-brand-gold">Referral code</p>
                  <b className="text-lg tracking-[.04em] text-brand-gold-deep">{member.referral_code}</b>
                </div>
                <Button size="sm" variant="ghost" onClick={copyCode} className="text-brand-gold-deep">
                  <Copy size={15} />Copy
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 min-[420px]:grid-cols-2">
              <Row label="Email" value={member.email} />
              <Row label="Phone" value={member.phone} />
              <Row label="City" value={member.city} />
              <Row label="State" value={member.state} />
              <Row label="Membership" value={roleLabel(member.role || role)} />
              <Row label="Tier" value={tier ? tier[0].toUpperCase() + tier.slice(1) : null} />
            </div>
            {role !== "admin" && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setPhoneOpen(true)}
                className="w-full rounded-[12px] border-black/10"
              >
                {member.phone ? "Change number" : "Add mobile number"}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[#0a2724] text-brand-gold-light">
                <CircleUserRound size={24} />
              </div>
              <div>
                <p className="text-[17px] font-semibold text-brand-ink">{roleLabel(role)}</p>
                <p className="text-sm text-brand-muted">Staff account</p>
              </div>
            </div>
            <p className="text-sm text-brand-muted">
              This account doesn't have a member profile — referral and reward details apply to member accounts only.
            </p>
          </div>
        )}

        <div className="space-y-2.5">
          <PushToggle />
          <InstallButton className="w-full border-black/10 bg-white" />
        </div>
      </DialogContent>
      {phoneOpen && (
        <PhoneGate
          dismissible
          initialPhone={member?.phone || ""}
          onDone={phoneDone}
          onDefer={() => setPhoneOpen(false)}
          onLogout={onLogout}
          onDismiss={() => setPhoneOpen(false)}
        />
      )}
    </Dialog>
  );
};
